import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SidebarNav from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import axios from 'axios';
import MyProjectCalendar from "../../components/programManager/MyProjectCalendar";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import PremiumCard from "../../components/common/PremiumCard";
import { motion, AnimatePresence } from "framer-motion";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import ReactECharts from "echarts-for-react";
import { 
  Clock, CheckCircle, FileText, Plus, TrendingUp, 
  PieChart as PieChartIcon, RefreshCw, Calendar, X 
} from "lucide-react";
import LeaveRequestForm from "../../components/LeaveRequestForm";

// === API Configuration ===
const BASE_URL = "https://ims.holdthechild.net/api";

export default function ProgramManagerDashboard() {
  const [projectTrendData, setProjectTrendData] = useState([]);
  const [taskStats, setTaskStats] = useState({
    pending: 0,
    completed: 0,
    inProgress: 0,
    contractsDue: 0,
  });
  const [budgetData, setBudgetData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showLeaveForm, setShowLeaveForm] = useState(false);

  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");
  const staffId = user ? user.id : null;

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!token) {
        navigate("/");
        return;
      }

      const config = { headers: { 'Authorization': `Bearer ${token}` } };

      try {
        setIsLoading(true);
        setIsError(false);

        // 1. Fetch Stats Summary
        const statsRes = await axios.get(`${BASE_URL}/projects/stats/summary`, config);
        const projectStats = statsRes.data?.data || {};
        setTaskStats({
          pending: projectStats['In Progress'] || 0,
          completed: projectStats.Completed || 0,
          inProgress: projectStats['In Progress'] || 0,
          contractsDue: projectStats.overdue || 0,
        });

        // 2. Fetch Projects for Trend Analysis
        const projectsRes = await axios.get(`${BASE_URL}/projects`, config);
        const allProjects = projectsRes.data?.data || [];

        const monthlyData = allProjects.reduce((acc, project) => {
          const month = new Date(project.startDate).toLocaleString('default', { month: 'short' });
          if (!acc[month]) acc[month] = { month, Complete: 0, Pending: 0, Assigned: 0 };
          if (project.status === 'Completed') acc[month].Complete += 1;
          else if (project.status === 'In Progress') {
            acc[month].Pending += 1;
            acc[month].Assigned += 1;
          } else if (project.status === 'Planning') acc[month].Assigned += 1;
          return acc;
        }, {});
        
        setProjectTrendData(Object.values(monthlyData));
        setBudgetData([
          { name: "Allocated", value: 400 },
          { name: "Spent", value: 300 },
          { name: "Remaining", value: 100 },
        ]);

      } catch (error) {
        console.error("Dashboard Fetch Error:", error);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [token, navigate]);

  // Chart Options
  const projectStatusChartOptions = {
    tooltip: { trigger: "item" },
    legend: { bottom: 0, left: 'center', icon: 'circle', textStyle: { fontSize: 10, fontWeight: 'bold' } },
    series: [{
      name: "Projects",
      type: "pie",
      radius: ["45%", "70%"],
      avoidLabelOverlap: false,
      itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 2 },
      label: { show: false },
      data: [
        { value: taskStats.completed, name: "Completed", itemStyle: { color: '#10b981' } },
        { value: taskStats.inProgress, name: "In Progress", itemStyle: { color: '#fbbf24' } },
        { value: taskStats.pending, name: "Pending", itemStyle: { color: '#f97316' } },
      ],
    }],
  };

  const budgetTrackingChartOptions = {
    tooltip: { trigger: 'item' },
    legend: { bottom: 0, left: 'center', icon: 'circle', textStyle: { fontSize: 10, fontWeight: 'bold' } },
    series: [{
      name: "Budget",
      type: "pie",
      radius: ['45%', '70%'],
      avoidLabelOverlap: false,
      itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 2 },
      label: { show: false },
      data: budgetData.map((item, index) => ({
        value: item.value,
        name: item.name,
        itemStyle: { color: ['#10b981', '#f97316', '#cbd5e1'][index] },
      })),
    }],
  };

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
      <SidebarNav 
        visible={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col items-center text-center mb-12">
              <PremiumHeader 
                title="Program" 
                lastWordAccent="Manager" 
                subtitle="Real-time oversight of project milestones and budget health." 
              />
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowLeaveForm(true)}
                className="mt-8 flex items-center gap-3 bg-orange-800 text-white px-10 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-orange-200 shadow-2xl shadow-gray-200 transition-all"
              >
                <Plus size={16} strokeWidth={3} /> Request Administrative Leave
              </motion.button>
            </div>

            {/* KPI Row */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {[
                { title: "Pending", val: taskStats.pending, icon: Clock, accent: "orange", desc: "Awaiting Action" },
                { title: "Completed", val: taskStats.completed, icon: CheckCircle, accent: "emerald", desc: "Successfully delivered" },
                { title: "In Progress", val: taskStats.inProgress, icon: RefreshCw, accent: "blue", desc: "Active Execution" },
                { title: "Contracts Due", val: taskStats.contractsDue, icon: FileText, accent: "red", desc: "Urgent renewals" }
              ].map((stat, i) => (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  key={i}
                >
                  <PremiumStatCard
                    title={stat.title}
                    value={stat.val}
                    icon={<stat.icon className={`text-${stat.accent}-600`} />}
                    iconBg={`bg-${stat.accent}-50`}
                    description={stat.desc}
                  />
                </motion.div>
              ))}
            </section>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              {/* Calendar */}
              <div className="lg:col-span-2">
                <PremiumCard title="Project Timeline" icon={<Calendar size={18} />}>
                  <div className="p-2">
                    <MyProjectCalendar />
                  </div>
                </PremiumCard>
              </div>

              {/* Charts Column */}
              <div className="flex flex-col gap-8">
                <PremiumCard title="Status Distribution" icon={<PieChartIcon size={18} />}>
                  <ReactECharts option={projectStatusChartOptions} style={{ height: "280px" }} />
                </PremiumCard>
                
                <PremiumCard title="Budget Health" icon={<TrendingUp size={18} />}>
                  <ReactECharts option={budgetTrackingChartOptions} style={{ height: "280px" }} />
                </PremiumCard>
              </div>
            </div>

            {/* Monthly Trends */}
            <PremiumCard title="Monthly Execution Trends" icon={<TrendingUp size={18} />}>
              <div className="h-[400px] mt-6 px-4">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={projectTrendData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="month" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fontWeight: 'bold', fill: '#94a3b8' }} 
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fontWeight: 'bold', fill: '#94a3b8' }}
                    />
                    <Tooltip 
                      contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '12px' }} 
                    />
                    <Legend iconType="circle" verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: '20px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.1em' }} />
                    <Line type="monotone" dataKey="Complete" stroke="#10b981" strokeWidth={4} dot={{ r: 4, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 8 }} />
                    <Line type="monotone" dataKey="Pending" stroke="#f97316" strokeWidth={4} dot={{ r: 4, fill: '#f97316', strokeWidth: 2, stroke: '#fff' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </PremiumCard>
          </div>
        </main>
      </div>

      {/* Leave Modal */}
      <AnimatePresence>
        {showLeaveForm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              onClick={() => setShowLeaveForm(false)} 
              className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
            />
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.95, opacity: 0, y: 20 }} 
              className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-gray-100"
            >
              <div className="px-10 py-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
                <div className="flex items-center gap-4">
                  <div className="size-10 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center">
                    <Plus size={20} strokeWidth={3} />
                  </div>
                  <h2 className="text-xs font-black text-gray-800 uppercase tracking-[0.2em]">Administrative Leave Request</h2>
                </div>
                <button 
                  onClick={() => setShowLeaveForm(false)} 
                  className="p-3 bg-white hover:bg-gray-100 rounded-2xl transition-all active:scale-95 border border-gray-100 shadow-sm"
                >
                  <X size={20} className="text-gray-400" />
                </button>
              </div>
              <div className="overflow-y-auto p-10 max-h-[calc(90vh-120px)]">
                <LeaveRequestForm staffId={staffId} token={token} onClose={() => setShowLeaveForm(false)} />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

